import type { JobTag } from '@seek/chalice-types';
import { DotSeparator } from '@seek/discover-ui';
import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  Inline,
  Link,
  List,
  Stack,
  Text,
  useResponsiveValue,
} from 'braid-design-system';
import { useCallback, useEffect, useState, type ComponentProps } from 'react';
import striptags from 'striptags';

import { LazyLoadedImg } from 'src/components/HomePage/LazyLoadedImg/LazyLoadedImg';
import {
  JOB_LISTING_POSITION_KEY,
  JOB_LISTING_NUMBER_OF_SELLING_POINT_KEY,
} from 'src/handlers/JobDetails';
import useSalaryDisplayLabel from 'src/hooks/useSalaryDisplayLabel/useSalaryDisplayLabel';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { useCareerFeedJobAiSummariesEnabled } from 'src/store/featureFlags/hooks';
import { getSearchParams } from 'src/utils/urlParams';

import type {
  RECOMMENDED_JOB_AI_VIEW_ORIGIN,
  RECOMMENDED_JOB_VIEW_ORIGIN,
} from '../../DashboardRecommendations/RecommendedJobCard';
import { useSignedInDashboardContext } from '../../SignedInDashboardContextProvider';

import translations from './.vocab';
import CTASectionView from './CTASectionView/CTASectionView';
import HideJobButton from './components/HideJobButton/HideJobButton';
import { JobBadges } from './components/JobBadges/JobBadges';
import { JobLabels } from './components/JobLabels';
import SaveJobButton from './components/SaveJobButton/SaveJobButton';
import type UnhideJobSection from './components/UnhideJobButton/UnhideJobButton';
import UnhideJobButton from './components/UnhideJobButton/UnhideJobButton';
import { useRecommendationBadges } from './hooks/useRecommendationBadges';

import * as styles from './JobCard.css';

export type Job = {
  solMetadata?: any;
  id: string;
  title: string;
  advertiserName: string;
  salaryLabel?: string;
  salaryCurrencyLabel?: string;
  locationLabel: string;
  listingDateLabel?: string;
  srcLogo?: string;
  isExpired?: boolean;
  isSaved?: boolean;
  tags?: JobTag[];
  bullets?: string[];
  enrichmentSummary?: string[];
};

interface JobCardProps {
  jobInfo: Job;
  analytics: {
    linkName: string;
    linkSection: string;
    position: number;
    solHash?: string;
    viewOrigin:
      | 'saved-homepage'
      | typeof RECOMMENDED_JOB_VIEW_ORIGIN
      | typeof RECOMMENDED_JOB_AI_VIEW_ORIGIN;
  };
  testAutomation?: {
    jobCardLink?: string;
  };
  isHidden?: boolean;
  isHomeFeedbackLoopsHideJobEnabled?: boolean;
  onSaveJobButtonClick?: ComponentProps<typeof SaveJobButton>['onClick'];
  onHideJobButtonClick?: ComponentProps<typeof HideJobButton>['onClick'];
  onUnhideJobButtonClick?: ComponentProps<typeof UnhideJobSection>['onClick'];
}

export const HOMEPAGE_JOB_CARD_LOGO_HEIGHT = 65;
const HOMEPAGE_JOB_CARD_LOGO_MIN_WIDTH = 65;
export const HOMEPAGE_JOB_CARD_LOGO_MAX_WIDTH = 100;

const constructPath = (
  path: string,
  location?: {
    queryParams?: Record<string, string>;
    solHash?: string;
  },
) => {
  const searchParams = location?.queryParams
    ? `?${getSearchParams(location.queryParams).toString()}`
    : '';

  const solHash = location?.solHash ? `#sol=${location.solHash}` : '';

  return `${path}${searchParams}${solHash}`;
};

const CARD_PADDING = 'medium';

const JobCard = ({
  jobInfo,
  onSaveJobButtonClick,
  onHideJobButtonClick,
  onUnhideJobButtonClick,
  isHidden,
  isHomeFeedbackLoopsHideJobEnabled,
  analytics: { linkName, linkSection, position, solHash, viewOrigin },
  testAutomation,
}: JobCardProps) => {
  const {
    advertiserName,
    id,
    isExpired,
    isSaved = false,
    listingDateLabel,
    locationLabel,
    salaryCurrencyLabel,
    salaryLabel,
    srcLogo,
    tags,
    title,
    bullets,
  } = jobInfo;

  const analyticsFacade = useAnalyticsFacade();

  const { t } = useTranslations(translations);
  const { badges, labels, viewedLabel } = useRecommendationBadges(
    tags,
    isExpired,
  );

  const salaryDisplayLabel = useSalaryDisplayLabel({
    currencyLabel: salaryCurrencyLabel,
    salary: salaryLabel,
  });

  const careerFeedJobAiSummariesEnabled = useCareerFeedJobAiSummariesEnabled();

  const jobUrl = useMelwaysLink({
    path: constructPath(isExpired ? `/expiredjob/${id}` : `/job/${id}`, {
      queryParams: {
        ref: viewOrigin,
        [JOB_LISTING_POSITION_KEY]: String(position),
        ...(careerFeedJobAiSummariesEnabled
          ? {
              [JOB_LISTING_NUMBER_OF_SELLING_POINT_KEY]: String(
                bullets?.length || 0,
              ),
            }
          : {}),
      },
      solHash,
    }),
  });

  const { setSelectedJob } = useSignedInDashboardContext();

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false,
  });

  const onJobCardClick = useCallback(
    (event: React.MouseEvent) => {
      analyticsFacade.linkClicked({
        href: jobUrl,
        linkName,
        linkContext: {
          jobId: id,
          linkPosition: String(position),
          linkSection,
        },
      });

      if (!isMobile) {
        event.preventDefault();
        event.stopPropagation();
        setSelectedJob({
          id,
          analytics: {
            origin: viewOrigin,
            position,
            solHash,
            ...(careerFeedJobAiSummariesEnabled
              ? {
                  sellingPoints: bullets?.length || 0,
                }
              : {}),
          },
        });
      }
    },
    [
      analyticsFacade,
      bullets?.length,
      careerFeedJobAiSummariesEnabled,
      id,
      isMobile,
      jobUrl,
      linkName,
      linkSection,
      position,
      setSelectedJob,
      solHash,
      viewOrigin,
    ],
  );

  const [shouldAnimate, setShouldAnimate] = useState(false);
  const isCardHidden = isHomeFeedbackLoopsHideJobEnabled && isHidden;

  useEffect(() => {
    if (isCardHidden) {
      setShouldAnimate(true);
    }
  }, [isCardHidden]);

  const CTATextContent = () =>
    isCardHidden ? (
      <Text
        size="small"
        weight="medium"
        id={`recs-homepage-unhide-job-message-${id}`}
      >
        {t("We won't recommend this job to you again")}
      </Text>
    ) : (
      <Box />
    );

  const CTAButtonsContent = () =>
    !isCardHidden
      ? onSaveJobButtonClick && (
          <Inline space="gutter" alignY="center">
            <SaveJobButton
              id={`recs-homepage-${isSaved ? 'unsave' : 'save'}-job-${id}`}
              isSaved={isSaved}
              onClick={onSaveJobButtonClick}
            />
            {isHomeFeedbackLoopsHideJobEnabled && onHideJobButtonClick && (
              <HideJobButton
                id={`recs-homepage-hide-job-${id}`}
                label={t('Hide')}
                onClick={onHideJobButtonClick}
              />
            )}
          </Inline>
        )
      : onUnhideJobButtonClick && (
          <UnhideJobButton
            id={`recs-homepage-unhide-job-${id}`}
            label={t('Undo')}
            onClick={onUnhideJobButtonClick}
          />
        );

  return (
    <Box
      aria-label={striptags(title)}
      position="relative"
      className={[
        styles.jobCardBase,
        styles.focusedJobCard,
        styles.hiddenJobCardOverlayBase,
        {
          [styles.hoveredJobCard]: !isCardHidden,
          [styles.hiddenJobCardOverlayFadeIn]: isCardHidden,
          [styles.hiddenJobCardOverlayFadeOut]: !isCardHidden && shouldAnimate,
        },
      ]}
      padding={CARD_PADDING}
    >
      <Link
        href={jobUrl}
        className={[
          styles.jobCardLink,
          isCardHidden ? styles.hiddenJobCardLink : null,
        ]}
        onClickCapture={onJobCardClick}
        data={
          testAutomation?.jobCardLink
            ? { automation: testAutomation.jobCardLink }
            : {}
        }
      />
      <Stack space="small">
        <Columns
          space={{ tablet: 'small', mobile: srcLogo ? 'small' : 'none' }}
          collapseBelow="tablet"
          reverse
        >
          <Column width="content">
            <Box
              style={{
                // Removing height to preserve logo spacing above mobile breakpoint
                height: srcLogo ? HOMEPAGE_JOB_CARD_LOGO_HEIGHT : 0,
                maxWidth: HOMEPAGE_JOB_CARD_LOGO_MAX_WIDTH,
                minWidth: HOMEPAGE_JOB_CARD_LOGO_MIN_WIDTH,
              }}
            >
              {srcLogo ? (
                <Box
                  display="flex"
                  height="full"
                  data-automation="job-card-image"
                >
                  <LazyLoadedImg src={srcLogo} alt="" />
                </Box>
              ) : null}
            </Box>
          </Column>
          <Column>
            <Stack space="small">
              <Stack space="xsmall">
                <Text size="large" weight="medium">
                  <Box
                    className={{
                      [styles.markVisited]: Boolean(viewedLabel),
                    }}
                    component="span"
                    data={{
                      automation: 'jobTitle',
                    }}
                  >
                    {title}
                  </Box>
                </Text>
                <Text
                  tone="secondary"
                  data={{
                    automation: 'jobAdvertiser',
                  }}
                >
                  {advertiserName}
                </Text>
              </Stack>
              <Stack space="small">
                <JobBadges badges={badges} />
                <Stack space="xsmall">
                  {locationLabel ? (
                    <Text size="small" tone="secondary">
                      {locationLabel}
                    </Text>
                  ) : null}
                  {salaryDisplayLabel ? (
                    <Text size="small" tone="secondary">
                      {salaryDisplayLabel}
                    </Text>
                  ) : null}
                </Stack>
              </Stack>
              {bullets?.length ? (
                <List size="small" space="xsmall">
                  {bullets.map((bullet, i) => (
                    <Text key={i} size="xsmall" tone="secondary">
                      {bullet}
                    </Text>
                  ))}
                </List>
              ) : null}
            </Stack>
          </Column>
        </Columns>
        {listingDateLabel ? (
          <Text size="xsmall" tone="secondary">
            {viewedLabel ? (
              <DotSeparator content={[listingDateLabel, viewedLabel]} />
            ) : (
              listingDateLabel
            )}
          </Text>
        ) : null}
        <JobLabels labels={labels} />
      </Stack>
      <Box
        position="absolute"
        right={0}
        bottom={0}
        paddingRight={CARD_PADDING}
        paddingBottom={CARD_PADDING}
        paddingLeft={CARD_PADDING}
        zIndex={isCardHidden ? 1 : 0}
        display="flex"
        alignItems="flexEnd"
        height="touchable"
        width="full"
        className={
          isCardHidden ? styles.unhideJobSection : styles.saveAndHideJobSection
        }
      >
        <CTASectionView
          textContent={<CTATextContent />}
          buttonsContent={<CTAButtonsContent />}
        />
      </Box>
    </Box>
  );
};

export default JobCard;
